<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">

    <div class="row no-gutter h-100">
      <div class="col-3" v-if="showList">
        <div v-if="showDateFilter">
          <ReportFilterPopover
            headerTitle="Task Filter"
            :showApplyButton="true"
            :showResetButton="true"
            :appendToBody="true"
            alignment="left"
            placement="bottom-end"
            @apply="$emit('dateApplied')"
            @reset="$emit('dateReset')">
            <template v-slot:popover>
              <ReportDateRangeSelect 
                :startDate="editingStartDate"
                :endDate="editingEndDate"
                @updateStartDate="editingStartDate = $event; $emit('updateEditingStartDate', $event)"
                @updateEndDate="editingEndDate = $event; $emit('updateEditingEndDate', $event)"
              />
            </template>
            <template v-slot:filterDisplay>
              <div>
                <i class="far fa-calendar mr-2"></i>{{startDate | formatDate}} - {{endDate | formatDate}}
              </div>                
            </template>
          </ReportFilterPopover>
        </div>
        <StaticDataTable class="static-table-list" v-if="hasChartData" :smallRows="true">
          <template v-slot:header>
            <tr>
              <th width="60%">{{taskTableData.nameTitle}}</th>
              <th width="40%">{{taskTableData.valueTitle}}</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(dataValue, index) of taskTableData.data" :key="index">
              <td>{{dataValue.name}}</td>
              <td><span v-html="dataValue.displayValue" /></td>
            </tr>
          </template>
        </StaticDataTable>
      </div>
      <div :class="showList ? 'col-9' : 'col-12'">
        <ChartDoughnut
          :series="taskSeries"
          :legend="taskLegend"
          :options="taskOptions"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import Utils from '@/utils/utils'
import AnalyticsDateMixin from '@/modules/intelligence/mixins/analyticsDate'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import ChartDoughnut from '@/components/charts/chartDoughnut'

import { ChartDateRangeTypes } from '@/modules/intelligence/types'

const TaskChartTypes = {
  lateArrivalUserCount: 'lateArrivalUserCount',
  lateArrivalUserAvgDuration: 'lateArrivalUserAvgDuration',
  lateArrivalDestinationCount: 'lateArrivalDestinationCount',
  lateArrivalDestinationAvgDuration: 'lateArrivalDestinationAvgDuration',
  onSiteUserAvgDuration: 'onSiteUserAvgDuration',
  onSiteDestinationAvgDuration: 'onSiteDestinationAvgDuration'
}

export default {
  name: 'TaskInfoChartWidget',
  mixins: [AnalyticsDateMixin],
  components: {
    AnalyticsWidget,
    ChartDoughnut,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    showList: Boolean,
    showDateFilter: Boolean,
    dateRangeType: {
      type: String,
      default: ChartDateRangeTypes.today
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    startDate: String,
    endDate: String
  },
  TaskChartTypes: TaskChartTypes,
  data() {
    return {
      taskSeries: [],
      taskTableData: {},
      taskLegend: [],
      taskOptions: {},

      editingStartDate: this.startDate,
      editingEndDate: this.endDate
    }
  },
  computed: {
    hasChartData() {
      if(this.taskSeries) {
        return this.taskSeries.some(series => {
          if(series.data && series.data.length > 0)
            return true
        })
      }
      return false
    },
    hasContactFilter() {
      return this.contactId != null
    }
  },
  mounted() {
    this.requestChartData()

    this.subscribeEndpoint('/tasks/taskmanager', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/tasks/taskmanager')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    },
    dateRangeType: function() {
      this.requestChartData()
    },
    startDate: function() {
      this.editingStartDate = this.startDate
      this.requestChartData()
    },
    endDate: function() {
      this.editingEndDate = this.endDate
      this.requestChartData()
    }
  },
  methods: {
    requestChartData() {
      var summaryFilter = {
        startDate: this.calculateStartDateForDateRangeType(this.dateRangeType, this.startDate).toDate().toISOString(),
        endDate: this.calculateEndDateForDateRangeType(this.dateRangeType, this.endDate).toDate().toISOString(),
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null,
        maxGroupSize: 10
      }

      this.$apiService.analyticsService.getTasksDespatchArrivalDepartureSummary(summaryFilter)
      .then(response => {
        let minutesFormatter = (value) => {
          return Utils.formatDecimal(parseFloat(value), 2, true) + ' mins'
        }
        
        if(this.type == TaskChartTypes.lateArrivalUserCount) {
          this.setDataSeries('Late Arrival Count', response.data.lateArrivalUserCountList)
          this.setDataTable('User', 'Late Arrival Count', response.data.lateArrivalUserCountList)
        }
        if(this.type == TaskChartTypes.lateArrivalUserAvgDuration) {
          this.setDataSeries('Late Arrival Avg Time', response.data.lateArrivalUserAvgDurationList, minutesFormatter)
          this.setDataTable('User', 'Late Arrival Avg Time', response.data.lateArrivalUserAvgDurationList, minutesFormatter)
        }
        if(this.type == TaskChartTypes.lateArrivalDestinationCount) {
          this.setDataSeries('Late Arrival Count', response.data.lateArrivalTaskItemCountList)
          this.setDataTable('Destination', 'Late Arrival Count', response.data.lateArrivalTaskItemCountList)
        }
        if(this.type == TaskChartTypes.lateArrivalDestinationAvgDuration) {
          this.setDataSeries('Late Arrival Avg Time', response.data.lateArrivalTaskItemAvgDurationList, minutesFormatter)
          this.setDataTable('Destination', 'Late Arrival Avg Time', response.data.lateArrivalTaskItemAvgDurationList, minutesFormatter)
        }
        if(this.type == TaskChartTypes.lateArrivalDestinationAvgDuration) {
          this.setDataSeries('Late Arrival Avg Time', response.data.lateArrivalTaskItemAvgDurationList, minutesFormatter)
          this.setDataTable('Destination', 'Late Arrival Avg Time', response.data.lateArrivalTaskItemAvgDurationList, minutesFormatter)
        }
        if(this.type == TaskChartTypes.onSiteUserAvgDuration) {
          this.setDataSeries('On Site Avg Time', response.data.timeOnSiteUserAvgDurationList, minutesFormatter)
          this.setDataTable('User', 'On Site Avg Time', response.data.timeOnSiteUserAvgDurationList, minutesFormatter)
        }
        if(this.type == TaskChartTypes.onSiteDestinationAvgDuration) {
          this.setDataSeries('On Site Avg Time', response.data.timeOnSiteUserAvgPerItemDurationList, minutesFormatter)
          this.setDataTable('Destination', 'On Site Avg Time', response.data.timeOnSiteUserAvgPerItemDurationList, minutesFormatter)
        }

        this.taskLegend.show = this.showLegend
      })
    },

    setDataSeries(name, dataList, valueFormatter) {
      if(!valueFormatter)
        valueFormatter = (value) => value

      this.taskOptions.tooltip = {
        formatter(param) {
          return `${param.seriesName} <br />${param.name} : ${valueFormatter(param.value)} (${param.percent}%)`
        }
      }
      
      this.taskSeries = [{
        name: name,
        data: dataList.map(group => {
          return {
            name: group.name,
            value: group.value
          }
        }),
        label: {
          show: true,
          formatter(param) {
            return param.name
          }
        },
      }]
      this.taskLegend = {
        data: dataList.map(group => group.name)
      }
    },
    setDataTable(nameTitle, valueTitle, dataList, valueFormatter) {
      if(!valueFormatter)
        valueFormatter = (value) => value

      this.taskTableData = {
        nameTitle: nameTitle,
        valueTitle: valueTitle,
        data: dataList.map(group => {
          return {
            name: group.name,
            displayValue: valueFormatter(group.value)
          }
        })
      }
    }
  }
}
</script>